import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import ModernChatPage from '../pages/ModernChatPage';
import { useGroupInfo } from '../../hooks/useGroupInfo';

// Create a basic theme matching the Mighty45 colors
const chatTheme = createTheme({
  palette: {
    primary: {
      main: '#0077c8', // Mighty45 blue
    },
    secondary: {
      main: '#1a2d66', // Mighty45 dark blue
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiModal: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
  },
});

interface ModernChatWrapperProps {
  uid: string;
  groupId?: string;
  width?: string | number;
  height?: string | number;
  maxHeight?: number;
  minHeight?: number;
  [key: string]: any; // For other props passed through to ModernChatPage
}

/**
 * ModernChatWrapper - Bridge component that provides the same interface as the old ChatComponent
 * while using the new ModernChatPage implementation underneath.
 * 
 * This allows us to drop-in replace ChatComponent usage without changing calling code.
 */
const ModernChatWrapper: React.FC<ModernChatWrapperProps> = ({
  uid,
  groupId,
  width = "auto",
  height = "600px",
  maxHeight = 600,
  minHeight = 400,
  ...otherProps
}) => {
  // Get the actual group name from Firebase
  const { groupName: fetchedGroupName, loading: groupNameLoading } =
    useGroupInfo(groupId || "");

  // Show better loading state instead of fallback group name
  const groupName = groupNameLoading
    ? "Loading..."
    : fetchedGroupName ||
      (groupId ? `Chat Group ${groupId.slice(-6)}` : "Modern Chat");

  // TODO: Pass uid to ModernChatPage when we integrate Firebase authentication
  console.log("Chat initialized for user:", uid);

  // Determine the height to use - ensure proper constraints for 100% height
  const isFullHeight = height === "100%";
  const containerHeight = isFullHeight
    ? "100%"
    : height !== "600px"
      ? height
      : typeof maxHeight === "number" && maxHeight > 0
        ? `${maxHeight}px`
        : "600px";
  const containerMinHeight = isFullHeight
    ? 0 // No min height when using 100%
    : height !== "600px"
      ? "auto"
      : typeof minHeight === "number" && minHeight > 0
        ? `${minHeight}px`
        : "400px";

  return (
    <ThemeProvider theme={chatTheme}>
      <CssBaseline />
      <Box
        sx={{
          width: width,
          height: containerHeight,
          minHeight: containerMinHeight,
          maxHeight: isFullHeight ? "100%" : undefined, // Constrain max height when using 100%
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // Prevent container from scrolling
          overflowAnchor: "none", // Prevent scroll anchoring that affects parent
          isolation: "isolate", // Create new stacking context to contain scrolling
          contain: "layout style size", // CSS containment to prevent effects on parent
          borderRadius: isFullHeight ? 0 : 2, // No border radius if full height
          backgroundColor: "#ffffff",
          border: isFullHeight ? "none" : "1px solid rgba(0, 119, 200, 0.1)",
          margin: 0, // Ensure no default margins
          padding: 0, // Ensure no default padding
          boxSizing: "border-box", // Ensure padding/borders are included in dimensions
          position: "relative", // Create positioning context
        }}
      >
        <ModernChatPage
          groupId={groupId}
          groupName={groupName}
          embedded={true} // Always embedded when used through wrapper
          {...otherProps}
        />
      </Box>
    </ThemeProvider>
  );
};

export default ModernChatWrapper;
